import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
    useGetDeviceRawDataQuery,
    useGetDeviceInfoQuery,
} from "./rawDataAPISlice";
import DarkDateRangePicker from "../DarkDateRangePicker";
import RawDataRegisterToggle from "./RawDataRegisterToggle";
import RawDataGraph from "./RawDataGraph";

export default function LandingPageRawData() {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { meter_id: device_id, serial_number } = useParams();
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState(dayjs().subtract(1, "day"));
    const [endDate, setEndDate] = useState(dayjs());
    const [register, setRegister] = useState("ActivePower");

    const request = {
        queryParams: {
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
            userTimezone,
            register,
        },
        deviceID: device_id,
    };

    const { data: deviceInfo } = useGetDeviceInfoQuery(device_id);
    const {
        data: deviceRawData,
        isLoading: isLoadingDeviceRawData,
        isSuccess: isSuccessDeviceRawData,
        isError: isErrorDeviceRawData,
        error: errorMessageDeviceRawData,
    } = useGetDeviceRawDataQuery(request);

    return (
        <Box sx={{ margin: "0 auto", padding: "70px 32px 40px" }}>
            {/* Page header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", pt: "28px", mb: "24px" }}>
                <ArrowBackIcon
                    sx={{ color: "rgba(255,255,255,0.6)", cursor: "pointer", flexShrink: 0, "&:hover": { color: "#fff" } }}
                    onClick={() => navigate(-1)}
                />
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.02em" }}>
                    Raw meter data
                </Typography>
                {deviceInfo && (
                    <Box sx={{ display: "flex", gap: "16px", ml: "8px", flexWrap: "wrap", alignItems: "center" }}>
                        <MetaChip label="SN" value={deviceInfo.serial_number ?? serial_number} />
                        {deviceInfo.make && <MetaChip label="Make" value={deviceInfo.make} />}
                        {deviceInfo.bec_number != null && <MetaChip label="BEC" value={deviceInfo.bec_number} />}
                    </Box>
                )}
            </Box>

            {/* Date range picker + Export CSV */}
            <Box sx={{ mb: "20px" }}>
                <DarkDateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onApply={(start, end) => { setStartDate(start); setEndDate(end); }}
                />
            </Box>

            {/* Register toggle */}
            <RawDataRegisterToggle register={register} setRegister={setRegister} />

            {/* Graph */}
            <RawDataGraph
                deviceInfo={deviceInfo}
                deviceRawData={deviceRawData}
                isSuccessDeviceRawData={isSuccessDeviceRawData}
                isErrorDeviceRawData={isErrorDeviceRawData}
                errorMessageDeviceRawData={errorMessageDeviceRawData}
                isLoadingDeviceRawData={isLoadingDeviceRawData}
                startDate={startDate}
                endDate={endDate}
            />
        </Box>
    );
}

function MetaChip({ label, value }) {
    return (
        <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)" }}>
            <Box component="span" sx={{ mr: "4px" }}>{label}</Box>
            <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>{value}</Box>
        </Typography>
    );
}
